<template>
  <div class="d-flex app-container">
    <navigator />
    <OnboardingWrapper class="achievements-wrapper">
      <div class="achievements-page">
        <div class="title-header">
          <h2>Achievements</h2>
        </div>

        <!-- Segmented Control -->
        <div class="segmented-control-wrapper">
          <div class="segmented-control">
            <div class="segmented-slider" :class="{ 'slide-right': showCompleted }"></div>
            <button
              class="segment-btn"
              :class="{ active: !showCompleted }"
              @click="showCompleted = false"
            >
              Incomplete
            </button>
            <button
              class="segment-btn"
              :class="{ active: showCompleted }"
              @click="toggleToCompleted"
            >
              Completed
            </button>
          </div>
        </div>

        <div class="achievements-content">
          <!-- Achievement Cards -->
          <div
            v-for="(achievement, index) in displayedAchievements"
            :key="'achievement-' + index"
            class="achievement-card"
          >
            <div class="card-container">
              <div class="achievement-info">
                <div class="achievement-title">{{ achievement.title }}</div>
              </div>

              <div class="achievement-icon-wrapper">
                <div class="achievement-icon" :class="{ 'completed': showCompleted }">
                  🏆
                </div>
              </div>

              <div class="achievement-description">
                {{ achievement.description }}
              </div>

              <div v-if="!showCompleted" class="progress-section">
                <div class="progress-label">{{ achievement.progression }}</div>
                <div class="progress">
                  <div
                    class="progress-bar"
                    role="progressbar"
                    :style="{ width: achievement.percentage + '%' }"
                    :aria-valuenow="achievement.percentage"
                    aria-valuemin="0"
                    aria-valuemax="100"
                  ></div>
                </div>
              </div>

              <div v-else class="completion-section">
                <div class="completion-badge">{{ achievement.level }}</div>
              </div>
            </div>
          </div>

          <!-- Coming Soon Cards -->
          <div
            v-for="n in comingSoonCount"
            :key="'coming-soon-' + n"
            class="achievement-card coming-soon"
          >
            <div class="card-container">
              <div class="achievement-info">
                <div class="achievement-title">Coming Soon</div>
                <div class="achievement-level">Stay Tuned!</div>
              </div>

              <div class="achievement-icon-wrapper">
                <div class="achievement-icon locked">🔒</div>
              </div>

              <div class="achievement-description">
                New achievements are on the way. Keep testing to unlock more rewards!
              </div>

              <div class="coming-soon-label">UPCOMING</div>
            </div>
          </div>
        </div>
      </div>
    </OnboardingWrapper>
  </div>
</template>

<script>
import navigator from "../layout/NavBarNew.vue";
import OnboardingWrapper from "../onboarding/OnboardingWrapper.vue";
import Achievements from "../../Firestore/Achievements.js";
import { onboardingService } from "@/services/OnboardingService";
import { initializePageOnboarding } from "@/services/onboardingConfigs";

export default {
  name: "AchievementsPage",
  components: { navigator, OnboardingWrapper },
  data() {
    return {
      userId: window.localStorage.getItem("userId"),
      userType: window.localStorage.getItem("userType")
        ? parseInt(window.localStorage.getItem("userType"))
        : -1,
      completedAchievements: [],
      incompletedAchievements: [],
      showCompleted: false,
      comingSoonCount: 6,
    };
  },
  computed: {
    displayedAchievements() {
      return this.showCompleted ? this.completedAchievements : this.incompletedAchievements;
    },
  },
  mounted() {
    if (!this.userId || this.userType === -1) {
      this.$router.push({ name: "login" });
      return;
    }
    this.fetchAchievements(this.userId);
    this.$nextTick(() => {
      setTimeout(() => {
        initializePageOnboarding('achievements', this.userType, onboardingService, this.$store);
      }, 800);
    });
  },
  methods: {
    async fetchAchievements(testerId) {
      try {
        const res = await Achievements.getAchievements(testerId);
        this.completedAchievements = res.data1;
        this.incompletedAchievements = res.data2;
      } catch (error) {
        console.error("Failed to load achievements:", error);
      }
    },
    toggleToCompleted() {
      this.showCompleted = true;
      if (this.completedAchievements.length > 0) {
        this.$nextTick(() => {
          setTimeout(() => {
            const userId = localStorage.getItem('userId');
            const key = `hasSeenOnboarding_achievementsCompleted_${userId}`;
            if (!localStorage.getItem(key)) {
              const completedConfig = [
                {
                  selector: '.completion-badge',
                  title: 'Completed Achievements 🎉',
                  message: "Great work! These badges show the level of achievements you've completed. Keep testing to earn more!",
                  position: 'left'
                }
              ];
              onboardingService.registerPageOnboarding('achievementsCompleted', completedConfig);
              onboardingService.startPageOnboarding('achievementsCompleted', userId);
            }
          }, 300);
        });
      }
    },
  },
};
</script>

<style scoped>
.app-container {
  height: 100vh;
  width: 100vw;
  overflow: hidden;
}

.achievements-wrapper {
  flex: 1;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
  background-color: #F5F5F5;
}

.achievements-page {
  font-family: 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;
  min-height: 100%;
}

/* ── Title ── */
.title-header {
  background-color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 85px;
  width: 100%;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.title-header h2 {
  color: #FF7222;
  font-weight: 600;
  font-size: 28px;
  margin: 0;
  letter-spacing: -0.5px;
}

/* ── Segmented Control ── */
.segmented-control-wrapper {
  display: flex;
  justify-content: center;
  padding: 28px 0 16px;
}

.segmented-control {
  position: relative;
  display: flex;
  background-color: #e8e8e8;
  border-radius: 12px;
  padding: 4px;
  gap: 0;
  width: 280px;
}

/* The sliding pill */
.segmented-slider {
  position: absolute;
  top: 4px;
  left: 4px;
  width: calc(50% - 4px);
  height: calc(100% - 8px);
  background: #FF7222;
  border-radius: 9px;
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 6px rgba(255, 114, 34, 0.35);
  pointer-events: none;
}

.segmented-slider.slide-right {
  transform: translateX(100%);
}

.segment-btn {
  flex: 1;
  position: relative;
  z-index: 1;
  background: transparent;
  border: none;
  border-radius: 9px;
  padding: 10px 0;
  font-size: 14px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  color: #888;
  transition: color 0.2s ease;
  user-select: none;
}

.segment-btn.active {
  color: white;
}

/* ── Card Grid ── */
.achievements-content {
  display: flex;
  flex-wrap: wrap;
  padding: 24px;
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  justify-content: center;
  gap: 0;
}

.achievement-card {
  background-color: white;
  border-radius: 12px;
  margin: 1rem;
  border: 1px solid #eaeaea;
  transition: box-shadow 0.2s;
  width: 250px;
  min-height: 370px;
  max-height: 370px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 20px;
  position: relative;
  display: flex;
  flex-direction: column;
}

.achievement-card:hover {
  box-shadow: 0 4px 8px rgba(255, 142, 77, 0.612);
}

.achievement-card.coming-soon {
  opacity: 0.7;
  background: linear-gradient(135deg, #ffffff 0%, #f9f9f9 100%);
}

.achievement-card.coming-soon:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.card-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: space-between;
}

.achievement-info { width: 100%; margin-bottom: 12px; }

.achievement-title {
  color: #333;
  font-weight: bold;
  font-size: 1.2rem;
  margin-bottom: 4px;
}

.achievement-level {
  color: #888;
  font-style: italic;
  font-size: 0.9rem;
}

.achievement-icon-wrapper {
  width: 100%;
  height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f0f0f0;
  border-radius: 8px;
  margin-bottom: 12px;
}

.achievement-icon {
  font-size: 80px;
  filter: grayscale(100%);
  opacity: 0.6;
  transition: all 0.3s ease;
}

.achievement-icon.completed {
  filter: grayscale(0%);
  opacity: 1;
  animation: celebrateIcon 0.5s ease;
}

.achievement-icon.locked {
  font-size: 60px;
  opacity: 0.4;
}

@keyframes celebrateIcon {
  0%, 100% { transform: scale(1); }
  50%       { transform: scale(1.1); }
}

.achievement-description {
  color: #666;
  font-size: 0.9rem;
  line-height: 1.5;
  margin-bottom: 12px;
  flex-grow: 1;
}

/* Progress */
.progress-section { width: 100%; margin-top: auto; }

.progress-label {
  font-size: 13px;
  font-weight: 600;
  color: #666;
  margin-bottom: 8px;
}

.progress {
  width: 100%;
  height: 24px;
  background-color: #E8E8E8;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.1);
}

.progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #FF7222, #FF9D5C);
  border-radius: 12px;
  transition: width 0.4s ease;
  box-shadow: 0 1px 3px rgba(255, 114, 34, 0.3);
}

/* Completion */
.completion-section {
  width: 100%;
  margin-top: auto;
  display: flex;
  justify-content: center;
}

.completion-badge {
  background: linear-gradient(135deg, #4CAF50, #45A047);
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  box-shadow: 0 2px 6px rgba(76, 175, 80, 0.3);
  letter-spacing: 0.3px;
  text-align: center;
}

/* Coming Soon */
.coming-soon-label {
  background: linear-gradient(135deg, #9E9E9E, #757575);
  color: white;
  padding: 10px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 13px;
  text-align: center;
  letter-spacing: 1px;
  margin-top: auto;
}

/* Responsive */
@media (max-width: 600px) {
  .achievement-card {
    width: calc(100% - 2rem);
    margin: 0.5rem 1rem;
  }
  .achievements-content { padding: 12px; }
  .segmented-control { width: 240px; }
}
</style>


// WEBPACK FOOTER //
// src/components/game-dashboard/Achievements.vue